// ============================================================================
// supportInbox.ts — SSoT клиентского ИНБОКСА ПОДДЕРЖКИ (§3.4): коды приложения/
// роли, подписи и иконки каналов, фильтры, распознавание событий шины и текст
// ошибок.
// Данные/права — SSoT СЕРВЕРА (SupportService): роль «Поддержка»/админ видят все
// обращения (фильтры/назначение/статусы/ответы/шаблоны), пользователь — только
// свои (урезанный набор действий). Клиент лишь скрывает недоступное (UX),
// сервер отклоняет запрещённое (PermissionDenied).
// ============================================================================

import { MessageCircle, MessagesSquare, Send } from 'lucide-react';
import type { SecurityAccess } from '@/lib/securityRoles';
import { friendlyError, isPermissionDeniedError } from '@/lib/errors';

/** Код приложения «Поддержка» (рендерер регистрируется КЛИЕНТОМ; без серверного объекта app). */
export const SUPPORT_APP_CODE = 'support';
/** Системная роль «Поддержка» (ядро: auth.EnsureSupportRole, код 'support'). */
export const SUPPORT_ROLE = 'support';

/** Размер страницы очереди (сервер: максимум 200, по умолчанию 50). */
export const TICKET_PAGE_SIZE = 50;

export interface SelectOption {
  value: string;
  label: string;
}

/** Статусы обращения (Значения — коды сервера: new | in_progress | resolved). */
export const TICKET_STATUS_OPTIONS: SelectOption[] = [
  { value: 'new', label: 'Новое' },
  { value: 'in_progress', label: 'В работе' },
  { value: 'resolved', label: 'Решено' },
];

/** Приоритет обращения (коды сервера: low | normal | high). */
export const TICKET_PRIORITY_OPTIONS: SelectOption[] = [
  { value: 'low', label: 'Низкий' },
  { value: 'normal', label: 'Обычный' },
  { value: 'high', label: 'Высокий' },
];

/**
 * Каналы-источники обращения (коды сервера: chat | telegram | max).
 * telegram/max — мосты транспортов ботов (приём через Telegram/MAX-подключение).
 */
export const TICKET_CHANNEL_OPTIONS: SelectOption[] = [
  { value: 'chat', label: 'Внутренний чат' },
  { value: 'telegram', label: 'Телеграм' },
  { value: 'max', label: 'MAX' },
];

export interface SupportTicketFilter {
  /** '' = все */
  status: string;
  /** '' = все | 'me' | 'none' */
  assigned: string;
  /** '' = все */
  channel: string;
  /** поиск по теме обращения */
  search: string;
}

export const EMPTY_TICKET_FILTER: SupportTicketFilter = {
  status: '',
  assigned: '',
  channel: '',
  search: '',
};

/**
 * Области очереди специалиста (явный выбор «Мои/Все» в «Кабинете»).
 * Значения совпадают с фильтром `assigned` сервера: '' | 'me' | 'none'.
 */
export const TICKET_SCOPE_OPTIONS: SelectOption[] = [
  { value: '', label: 'Все обращения' },
  { value: 'me', label: 'Мои обращения' },
  { value: 'none', label: 'Без исполнителя' },
];

/** Подпись статуса; неизвестное значение показываем как есть (SSoT сервера). */
export function supportStatusLabel(status: string): string {
  return TICKET_STATUS_OPTIONS.find((o) => o.value === status)?.label ?? status;
}

/** Подпись приоритета; неизвестное значение показываем как есть. */
export function supportPriorityLabel(priority: string): string {
  return TICKET_PRIORITY_OPTIONS.find((o) => o.value === priority)?.label ?? priority;
}

/** Подпись канала ('' = внутренний чат — канал по умолчанию). */
export function supportChannelLabel(channel: string): string {
  if (!channel) return 'Внутренний чат';
  return TICKET_CHANNEL_OPTIONS.find((o) => o.value === channel)?.label ?? channel;
}

/** Иконка канала-источника (как у плиток типов объектов — без рамочки). */
export type SupportChannelIcon = React.ComponentType<{ className?: string }>;

/**
 * Иконка канала ('' и 'chat' — внутренний чат, telegram, max).
 * Неизвестный канал — null: подпись показывается как есть, БЕЗ выдуманной иконки.
 */
export function supportChannelIcon(channel: string): SupportChannelIcon | null {
  switch (channel || 'chat') {
    case 'chat': return MessagesSquare;
    case 'telegram': return Send;
    case 'max': return MessageCircle;
    default: return null;
  }
}

/** Цветовой бейдж статуса (темизируемые классы Tailwind). */
export function supportStatusClass(status: string): string {
  if (status === 'new') return 'border-primary/40 bg-primary/10 text-primary';
  if (status === 'in_progress') return 'border-amber-500/40 bg-amber-500/10 text-amber-700';
  if (status === 'resolved') return 'border-green-600/40 bg-green-600/10 text-green-700';
  return 'border-border bg-muted/40 text-muted-foreground';
}

/** Цветовой бейдж приоритета (высокий — внимание, обычный/низкий — спокойные). */
export function supportPriorityClass(priority: string): string {
  if (priority === 'high') return 'border-destructive/40 bg-destructive/10 text-destructive';
  if (priority === 'low') return 'border-border bg-muted/40 text-muted-foreground';
  return 'border-border bg-muted/30 text-muted-foreground';
}

/** Доступ к полному инбоксу: администратор ИЛИ роль «Поддержка» (SSoT прав — сервер). */
export function isSupportStaff(access: SecurityAccess): boolean {
  return access.isAdmin || access.roles.includes(SUPPORT_ROLE);
}

/** События шины инбокса: поддержка live-обновления очереди/карточки. */
export function isSupportTicketEvent(topic: string): boolean {
  return topic === 'support.ticket.created' || topic.startsWith('support.ticket.updated.');
}

/** id обращения из payload события шины ('' — если поле отсутствует). */
export function ticketIdFromEventPayload(payload: unknown): string {
  if (!payload || typeof payload !== 'object') return '';
  const id = (payload as Record<string, unknown>)['id'];
  return typeof id === 'string' ? id : '';
}

/** Текст ошибки для пользователя: запрет прав — понятной фразой, остальное — friendlyError. */
export function supportErrorText(e: unknown): string {
  if (isPermissionDeniedError(e)) {
    return 'Недостаточно прав: действие доступно специалистам поддержки.';
  }
  return friendlyError(e);
}
